/**
 * Загрузка моков из реальных шаблонов Braining.
 *
 * JSON собран скриптом `scripts/extract-templates.py` и воспроизводит исходные
 * книги до рубля. Здесь только приведение к типам ядра: добавляем строкам
 * идентификаторы, которых в таблице нет и быть не может.
 *
 * Когда появится бэкенд, этот модуль заменяется серверными функциями,
 * а формат данных остаётся тем же.
 */

import { buildRateIndex, resolveRate, type PartnerProgram } from "@/calc";
import type { Block, Ratecard, ResourceLine, Stage, TaxScheme } from "@/calc";
import type { BrainingEstimate } from "@/braining/types";
import type { LearningLogEntry } from "@/braining/library-suggestions";

import { DEMO_CONTRACTOR, DEMO_PROVENANCE, DEMO_QUESTIONS, DEMO_SOURCES } from "./assistant";
import blocksLibraryJson from "./blocks-library.json";
import demoProjectJson from "./demo-project.json";
import ratecardJson from "./ratecard.json";

export const ratecard = ratecardJson as Ratecard;

export interface ProjectType {
  id: string;
  name: string;
  taxScheme: string;
  stages: { name: string; blocks: string[] }[];
  blockIds: string[];
}

/** Библиотека блоков работ — то, что у Braining играет роль справочника услуг. */
export interface BlocksLibrary {
  source: string;
  note: string;
  /**
   * Версия зерна, доехавшего до этого справочника.
   *
   * По ней видно, приезжало ли пополнение из `library-seed.ts`. У справочников,
   * засеянных до появления версии, поля нет — они получат пополнение при
   * первом чтении.
   */
  version?: string;
  /**
   * Отметка последней записи — как `savedAt` у сметы.
   *
   * Справочник правят с нескольких экранов сразу: «Обучение» применяет
   * предложения само, менеджер рядом правит блок, третий заводит роль. Пока
   * запись шла без условия, каждая из них отправляла свой документ целиком, и
   * последняя затирала остальные: выученные блоки, журнал и политика обучения
   * пропадали, а «Обучение» применяло их заново на следующем заходе.
   *
   * Теперь запись проходит, только если в базе лежит та же отметка, с которой
   * документ читали. Иначе вызывающему возвращается актуальный справочник.
   */
  updatedAt?: string;
  /**
   * Блоки, которые агентство убрало из справочника.
   *
   * Пополнение из зерна умеет только добавлять, и «удалённый» от «нового» оно
   * не отличает: блок, убранный с экрана, возвращался при первой же выкатке с
   * новой версией зерна. Список снятых id — это память о решении агентства, и
   * пополнение его уважает.
   */
  removedBlocks?: string[];
  /**
   * Типы, которые мы завели по ошибке и снимаем.
   *
   * Пополнение справочника умеет только добавлять — это защита правок
   * агентства, и менять её нельзя. Но у правила есть обратная сторона: наш
   * собственный мусор из него тоже не вычищается. «Пример с рейтами» — имя
   * листа исходной книги, уехавшее в выпадающий список типов проектов у
   * менеджера; блок под ним настоящий, с 23 наблюдениями, и терять его нельзя.
   *
   * Поэтому снятие типа объявляется явно и вместе с тем, куда переезжают его
   * блоки. Снимаем только своё: тип, которого нет, молча пропускается.
   */
  retiredTypes?: { id: string; moveBlocksTo: string }[];
  taxSchemes: TaxScheme[];
  /**
   * Ставки возвратной комиссии площадок.
   *
   * Прайс партнёра, а не наше решение: его сверяют с присланной презентацией и
   * правят целиком. Лежит в справочнике рядом с налоговыми схемами — это тоже
   * внешние условия, от которых зависит расчёт.
   */
  partnerProgram?: PartnerProgram;
  /** отделы агентства */
  divisions: string[];
  /** подотделы внутри Comm — по ним идёт план рентабельности */
  subdivisions: string[];
  keyRate: number;
  deferralDays: number[];
  projectTypes: ProjectType[];
  blocks: LibraryBlock[];
  /**
   * Что система вправе применять сама, по видам предложений.
   *
   * Пусто — работает умолчание из `library-suggestions.ts`: само применяется
   * то, что ничего не ломает (новая роль, новый блок), спрашивается то, что
   * двигает деньги (часы, ставки).
   */
  learningPolicy?: Record<string, "auto" | "ask">;
  /** журнал обучения: что приняли, что отклонили, что система сделала сама */
  learningLog?: LearningLogEntry[];
  /**
   * Типы работ, различённые в архиве калькуляторов.
   *
   * Полный список того, что агентство когда-либо считало, — шире справочника:
   * в справочник идёт типовое, а в архиве есть и разовое, и строки закупки,
   * записанные как блок. Тип с `blockId` уже лёг на блок; без него — то, чего
   * в справочнике нет, и «Обучение» предлагает такое завести.
   *
   * Пересобирается `scripts/build-library.py`, сверка — `library-coverage.py`.
   */
  corpusTypes?: CorpusType[];
}

/**
 * Тип работы по архиву: как его называли, сколько раз считали и почём.
 *
 * От `BlockStats` отличается тем, что живёт сам по себе, а не при блоке:
 * блока под него может и не быть — в этом весь смысл.
 */
export interface CorpusType {
  id: string;
  name: string;
  count: number;
  medianNet: number;
  p25: number;
  p75: number;
  costLines: number;
  confidence: BlockStats["confidence"];
  /** как эту работу называли в книгах */
  aliases: string[];
  /** типовой состав ролей с часами */
  lines: CorpusLine[];
  /** блок справочника, на который тип лёг; пусто — не лёг ни на один */
  blockId?: string;
}

/** Строка эталонного блока: та же строка сметы, но без идентификатора. */
export type LibraryLine = Omit<ResourceLine, "id"> & {
  /** когда строка появилась в эталоне — для пометки «new» */
  newAt?: string;
};

/**
 * Что блок стоил в архиве.
 *
 * Снято скриптом с рабочих книг агентства: `count` — в скольких сметах блок
 * встретился, `medianNet` и квартили — во что он там обходился. Это не цена и
 * не рекомендация, а порядок величины: «по архиву ≈ 225 тыс.» отвечает на
 * вопрос «мы вообще в ту сторону считаем?».
 */
export interface BlockStats {
  count: number;
  medianNet: number;
  p25: number;
  p75: number;
  costLines: number;
  confidence: "high" | "medium" | "low";
}

/**
 * Строка блока по архиву: роль, типовой объём и в скольких сметах она была.
 *
 * `seenIn` важнее `qty`: роль из двух смет — случай конкретного проекта, из
 * тринадцати — состав работ.
 */
export interface CorpusLine {
  name: string;
  unit: string;
  qty: number;
  seenIn: number;
  priceSource: ResourceLine["priceSource"];
}

export type LibraryBlock = Omit<Block, "lines"> & {
  projectType: string;
  lines: LibraryLine[];
  /** сводка по архиву; у блоков, заведённых руками, её нет */
  stats?: BlockStats;
  /** как этот блок называли в книгах — для сопоставления при разборе */
  aliases?: string[];
  /** типовой состав по архиву — для подстановки часов в надиктованный блок */
  corpusLines?: CorpusLine[];
  /** когда блок завели — для пометки «new» на экране справочника */
  newAt?: string;
  /**
   * Идёт ли блок в предложение по умолчанию.
   *
   * Блок, выученный из смет, в каркас сам не лезет: он появился один раз у
   * одного менеджера, и ставить его всем проектам этого типа — значит менять
   * предложение молча. `option` показывает его в списке снятой галочкой.
   */
  defaultRole?: "core" | "option";
};

/**
 * Зерно справочника: 18 блоков, снятых скриптом с шаблонов Braining.
 *
 * С переездом справочника в базу (0013) это уже не действующая библиотека, а
 * то, чем её наполняют при первом чтении. Живая приходит из стора и правится
 * на экране — поэтому функции ниже принимают её параметром, а мок оставлен
 * значением по умолчанию: тестам ядра он и нужен, они сверяются с исходными
 * книгами.
 */
export const library = blocksLibraryJson as unknown as BlocksLibrary;

export const taxSchemes = library.taxSchemes;

/**
 * Налоговая схема сметы, с запасной.
 *
 * Схемы лежат в справочнике, а справочник правится: схему можно
 * переименовать, а смета со старым идентификатором остаётся. Пока функция
 * бросала, одна такая смета роняла не себя, а всё вокруг — список
 * калькуляторов у каждого, страницу сметы и «Обучение», потому что расчёт
 * зовётся в цикле по всем сметам.
 *
 * Поэтому здесь запасная схема — первая из справочника, — а о подмене
 * говорит `taxSchemeMissing`: экран показывает это словами, и менеджер
 * выбирает схему заново. Расчёт по чужой ставке НДС тихо был бы хуже, чем
 * расчёт по первой с honest-пометкой.
 */
export function taxScheme(id: string, lib: BlocksLibrary = library): TaxScheme {
  const found = lib.taxSchemes.find((s) => s.id === id);
  if (found) return found;

  const fallback = lib.taxSchemes[0];
  if (!fallback) throw new Error("в справочнике нет ни одной налоговой схемы");
  return fallback;
}

/** Схемы с таким идентификатором в справочнике нет — считаем по запасной. */
export function taxSchemeMissing(id: string, lib: BlocksLibrary = library): boolean {
  return !lib.taxSchemes.some((s) => s.id === id);
}

/**
 * Идентификатор новой сущности.
 *
 * Счётчик в модуле не годится: он обнуляется при перезагрузке страницы, а сметы
 * лежат в localStorage и переживают её. Создал смету, перезагрузил, создал
 * вторую — и обе получали `est-1`, после чего список показывал дубли ключей,
 * а открытие по ссылке попадало не в ту смету.
 */
const nextId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function withIds(blockId: string, lines: Omit<ResourceLine, "id">[]): ResourceLine[] {
  return lines.map((l, i) => ({ ...l, id: `${blockId}:${i}` }));
}

/** Блок из библиотеки, готовый к вставке в смету. */
export function blockFromLibrary(blockId: string, lib: BlocksLibrary = library): Block | undefined {
  const src = lib.blocks.find((b) => b.id === blockId);
  if (!src) return undefined;
  const id = nextId(src.id);
  return {
    id,
    libraryId: src.id,
    name: src.name,
    goal: src.goal,
    result: src.result,
    // Семейство едет в смету вместе с блоком: по нему в шапке предлагаются
    // соседние варианты, и справочник для этого перечитывать не нужно.
    family: src.family,
    variant: src.variant,
    enabled: true,
    lines: withIds(id, src.lines),
  };
}

/**
 * Блок — в этап типа проекта.
 *
 * Состав типа живёт в `blockIds`, размещение по этапам — в `stages[].blocks`.
 * Писали их разные функции: новый блок попадал в состав, но ни в один этап, и
 * на «Брифе» ложился в первый попавшийся. Теперь всякий, кто добавляет блок
 * в тип, размещает его и по этапам — по умолчанию в последний, как и блок,
 * набранный руками в смете. У типа без этапов размещать нечего.
 */
export function placeInStage(type: ProjectType, blockId: string, stageIndex?: number): ProjectType {
  // Блок в этапе — блок в составе: третьего списка быть не должно.
  const blockIds = type.blockIds.includes(blockId) ? type.blockIds : [...type.blockIds, blockId];
  if (type.stages.length === 0) return { ...type, blockIds };
  const target = Math.min(stageIndex ?? type.stages.length - 1, type.stages.length - 1);
  return {
    ...type,
    blockIds,
    stages: type.stages.map((s, i) => ({
      ...s,
      blocks:
        i === target
          ? s.blocks.includes(blockId)
            ? s.blocks
            : [...s.blocks, blockId]
          : s.blocks.filter((id) => id !== blockId),
    })),
  };
}

/** Индекс этапа, где размещён блок; −1 — ни в одном. */
export function stageIndexOf(type: ProjectType, blockId: string): number {
  return type.stages.findIndex((s) => s.blocks.includes(blockId));
}

/** В каком этапе типа проекта лежит блок библиотеки. */
/**
 * В каком этапе типа стоит блок; `-1` — блока в этом типе нет.
 *
 * Различать важно: блок чужого типа при смене типа ложился в первый этап, то
 * есть в «Исследование и аналитику» поверх работ, к которым он не относится.
 * Вызывающий решает сам, куда его девать; у сметы это последний этап — там
 * же, где оказывается блок, добавленный руками.
 */
export function stageOfBlock(
  projectTypeId: string,
  blockId: string,
  lib: BlocksLibrary = library,
): number {
  const type = lib.projectTypes.find((p) => p.id === projectTypeId);
  if (!type) return -1;
  return type.stages.findIndex((s) => s.blocks.includes(blockId));
}

/**
 * Этапы сметы по типу проекта.
 *
 * Тип без этапов даёт один этап «Проект»: этапы — это ячейки, куда ложатся
 * блоки, и без единой ячейки блок из справочника пропадал молча — галочка на
 * «Брифе» не вставала, «Перейти к расчёту» не открывался никогда. Так было
 * у «Стратегической сессии» из зерна и у любого типа, заведённого в
 * справочнике руками.
 */
export function stagesForType(type: ProjectType): Stage[] {
  if (type.stages.length === 0) return [{ id: "stage-0", name: "Проект", blocks: [] }];
  return type.stages.map((s, i) => ({ id: `stage-${i}`, name: s.name, blocks: [] }));
}

/** Пустая смета выбранного типа проекта — со структурой этапов, но без блоков. */
export function emptyEstimate(
  projectTypeId: string,
  lib: BlocksLibrary = library,
): BrainingEstimate {
  const type = lib.projectTypes.find((p) => p.id === projectTypeId);
  if (!type) throw new Error(`неизвестный тип проекта: ${projectTypeId}`);
  return {
    id: nextId("est"),
    title: "",
    client: "",
    projectType: type.id,
    division: "Comm",
    // Подотдел не угадываем: от него идёт планка, и «Brands по умолчанию» был
    // бы решением за менеджера. Пустой — считаем по типу проекта.
    taxSchemeId: type.taxScheme,
    stages: stagesForType(type),
    deferral: { enabled: false, days: 30, advanceShare: 0, keyRate: lib.keyRate },
    sources: [],
    // Уточнений у пустой сметы нет и взяться им неоткуда: их задаёт разбор
    // материалов, прочитав документы проекта. Раньше сюда клонировался
    // демонстрационный пакет, и свежий калькулятор спрашивал про внешнюю
    // панель ещё до того, как увидел бриф.
    questions: [],
    assumptions: [],
  };
}

/**
 * Смета ровно как в исходной книге: 4 этапа, 13 блоков, 60 строк,
 * итог 10 821 700 ₽ без НДС.
 *
 * Никакого демонстрационного обвеса — ни подрядчика, ни бюджета, ни
 * вымышленного клиента. На ней держится проверка, что моки воспроизводят
 * шаблон до рубля; демо-версия строится поверх.
 */
export function templateEstimate(): BrainingEstimate {
  const src = demoProjectJson as unknown as {
    id: string;
    title: string;
    client: string;
    projectType: string;
    taxScheme: string;
    division: string;
    subdivision?: string;
    deferral: { enabled: boolean; days: number; advanceShare: number; keyRate: number };
    stages: {
      name: string;
      blocks: (Omit<Block, "lines"> & { lines: Omit<ResourceLine, "id">[] })[];
    }[];
  };

  const stages: Stage[] = src.stages.map((s, i) => ({
    id: `stage-${i}`,
    name: s.name,
    blocks: s.blocks.map<Block>((b) => ({
      id: b.id,
      libraryId: b.id,
      name: b.name,
      goal: b.goal,
      result: b.result,
      enabled: true,
      lines: withIds(b.id, b.lines),
    })),
  }));

  return {
    id: src.id,
    title: src.title,
    client: src.client,
    projectType: src.projectType,
    division: src.division,
    subdivision: src.subdivision,
    taxSchemeId: src.taxScheme,
    stages,
    deferral: src.deferral,
    targetMargin: 0.7,
    questions: structuredClone(DEMO_QUESTIONS),
    assumptions: [],
  };
}

/**
 * Внешние исполнители примера.
 *
 * Подобраны по практике архива: на сторону отдают дизайнеров, креаторов,
 * копирайтеров и исследовательские работы — примерно 40% денег сметы. Где
 * расход не назван явно, он считается от цены обратным ходом по наценке
 * перепродажи; наценки взяты разными по ролям вокруг архивной медианы 1,54×,
 * иначе у всех строк выходит одинаковая рентабельность 35%, и расход выглядит
 * не заведённым, а вычисленным из клиентской цены.
 */
const DEMO_FREELANCE: { block: RegExp; line: RegExp; cost?: number; markup?: number }[] = [
  { block: /с топ-менеджментом$/, line: /^Разработка дизайна/, cost: 60_000 },
  { block: /с топ-менеджментом$/, line: /^Проведение интервью/, cost: 230_000 },
  { block: /сотрудники/, line: /^Разработка дизайна/, cost: 60_000 },
  { block: /сотрудники/, line: /^Проведение интервью/, cost: 350_000 },
  { block: /^Отчет по блоку/, line: /^Дизайнер$/, markup: 1.6 },
  { block: /^Стратегическая сессия/, line: /^Модератор$/, cost: 195_000 },
  { block: /^Стратегическая сессия/, line: /^Дизайнер$/, markup: 1.6 },
  { block: /^Разработка EVP/, line: /^Дизайнер$/, markup: 1.6 },
  { block: /^Креативная и визуальная/, line: /^(Дизайнер|Арт директор)$/, markup: 1.7 },
  { block: /^Креативная и визуальная/, line: /^Креатор$/, markup: 1.45 },
  { block: /^Креативная и визуальная/, line: /^Копирайтер$/, markup: 1.5 },
  { block: /^HR-гайдбук/, line: /^Дизайнер$/, markup: 1.6 },
  { block: /^HR-гайдбук/, line: /^Копирайтер$/, markup: 1.5 },
  { block: /внутренних каналов/, line: /^Дизайнер$/, markup: 1.6 },
  { block: /внутренних каналов/, line: /^Креатор$/, markup: 1.45 },
  { block: /внешних каналов/, line: /^(Дизайнер|Креатор)$/, markup: 1.45 },
  { block: /внешних каналов/, line: /^Медиастратег$/, markup: 1.4 },
];

const RESALE_MARKUP = 1.54;

/**
 * Предложение исполнителей: часть строк блока уходит фрилансерам и подрядчику.
 *
 * Прототипная заглушка на месте будущей выучки из архива: расходы «как будто»
 * достали из материалов проекта и прошлых смет. Живёт отдельно от
 * `blockFromLibrary`, потому что справочник — это справочник: тесты сверяют
 * его с исходными книгами, где никаких исполнителей нет.
 */
export function proposeExecutors(block: Block, card: Ratecard = ratecard): Block {
  const rateIndex = buildRateIndex(card);

  return {
    ...block,
    lines: block.lines.map((l) => {
      if (l.name === DEMO_CONTRACTOR.lineName) {
        return {
          ...l,
          invoiceCost: DEMO_CONTRACTOR.cost,
          contractor: {
            mode: "markup" as const,
            rate: 0.4,
            supplier: DEMO_CONTRACTOR.supplier,
            sourceId: DEMO_CONTRACTOR.sourceId,
          },
        };
      }
      const hit = DEMO_FREELANCE.find((f) => f.block.test(block.name) && f.line.test(l.name));
      if (!hit) return l;
      const cost =
        typeof hit.cost === "number"
          ? hit.cost
          : Math.round(
              (resolveRate(l, rateIndex).rate * l.qty) / (hit.markup ?? RESALE_MARKUP) / 100,
            ) * 100;
      return { ...l, freelance: {}, invoiceCost: cost };
    }),
  };
}

/**
 * Демонстрационная смета: шаблон плюс всё, что нужно для показа — вымышленный
 * клиент, материалы с происхождением, названный бюджет и внешние исполнители.
 *
 * Подрядчик цепляется к конкретной строке, а не к блоку: в «Количественном
 * исследовании внешнем» три строки — наши часы по рейткарте, и только внешняя
 * панель уходит на сторону. Итоговая экономика выходит на ~31% себестоимости
 * и рентабельность ~69% при плане 70% — как у реальных смет, а не как у
 * шаблона, где расходов нет вовсе.
 */
export function demoEstimate(): BrainingEstimate {
  const base = templateEstimate();

  const stages: Stage[] = base.stages.map((s) => ({
    ...s,
    blocks: s.blocks.map((b) => proposeExecutors(b)),
  }));

  return {
    ...base,
    // Клиент вымышленный: демонстрационные данные не должны выглядеть как
    // утечка чьей-то реальной сметы.
    title: "Бренд работодателя 2027",
    client: "Вертикаль",
    // 20 млн с НДС — ровно та цифра, которую Аня называла на встрече как
    // типичную вводную: «смета должна выйти на 20 миллионов с НДС».
    budget: { amount: 20_000_000, withVat: true, hard: false, scope: "project" },
    deadline: "2026-08-25",
    contextNote:
      "Особое внимание на расчёт подрядчика по внешней панели — там уже есть точная цена.",
    sources: structuredClone(DEMO_SOURCES),
    provenance: structuredClone(DEMO_PROVENANCE),
    sourceFolder: "https://drive.google.com/drive/folders/1kY…-HR-brand",
    stages,
    // Демо уже «в работе»: расчёт нажат, слепок предложения записан. Свежая
    // смета, собранная через мастер, проходит кнопку «Рассчитать» сама.
    calculatedAt: "2026-08-13T10:00:00.000Z",
    proposal: {
      at: "2026-08-13T10:00:00.000Z",
      stages: structuredClone(stages),
      totalNet: 10_822_100,
    },
  };
}
